<script lang="ts" setup>
import { computed, ref } from 'vue';
import {
  BAlert,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import { useMutation } from '@vue/apollo-composable';
import { InviteCreateDocument, OrganizationType } from '@/gql/graphql';
import {
  OrganizationMembershipModeSelectOptions,
  OrganizationUserRoleSelectOptions,
  ORGANIZATION_USER_ROLES,
} from '@/constants';

// --- Props for the component ---
interface Props {
  organization: OrganizationType;
  show: boolean;
  adminView?: boolean;
}
const props = defineProps<Props>();
const emits = defineEmits(['success', 'close']);

// --- Computed Properties ---
const show = computed<boolean>(() => {
  return props.show;
});

// --- Data for new member invitation ---
const email = ref('');
const role = ref<string>(ORGANIZATION_USER_ROLES.MEMBER);
const message = ref('');
const description = ref('');

// --- Mutation: Switch Active Organization ---
const mutationInProgress = ref(false);
const mutationShowSuccess = ref(false);
const mutationError = ref<string | null>(null);

const {
  mutate: inviteCreateMut,
  onDone: onInviteCreateDone,
  onError: onInviteCreateError,
} = useMutation(InviteCreateDocument);

onInviteCreateDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.inviteCreate) {
    console.debug('Invite created:', res.data.inviteCreate);
    mutationShowSuccess.value = true;

    email.value = '';
    role.value = ORGANIZATION_USER_ROLES.MEMBER;
    message.value = '';
    description.value = '';

    emits('success', res.data.inviteCreate);

    setTimeout(() => {
      mutationShowSuccess.value = false;
      handleCloseModal();
    }, 1500);
  } else {
    console.error('Invitation failed:', res.data);
    mutationError.value = 'Invitation failed: ' + res.data;
  }
});

onInviteCreateError((err) => {
  console.error('Error creating invite:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers
function createInvite() {
  // Invite new user to organization
  mutationInProgress.value = true;
  mutationError.value = null;

  // Validate
  if (!props.organization?.id) {
    console.error('No organization to invite user to');
    mutationInProgress.value = false;
    mutationError.value = 'No organization to invite user to';
    return;
  }

  // Execute mutation
  console.log('Inviting user to organization:', props.organization.id);
  inviteCreateMut({
    invitedEmail: email.value,
    invitedOrganizationId: props.organization.id,
    invitedOrganizationUserRole: role.value,
    message: message.value,
    description: description.value,
  });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <b-modal
    v-if="props.organization"
    class="orgActivateModal"
    v-model="show"
    @hide="handleCloseModal"
    hide-footer
  >
    <!-- Title -->
    <template #title>
      <font-awesome-icon :icon="['fas', 'user-plus']" class="me-2" />
      <strong>Invite New User</strong>
    </template>
    <!-- Content -->
    <div>
      <BForm @submit.prevent="createInvite">
        <p>
          Invite a new user to join
          <strong>{{ props.organization.name }}</strong> organization. The
          invited user will receive an email with instructions to accept the
          invitation and join the organization. You can assign them a role of
          Member, Admin, or Owner to control their level of access and
          permissions within the organization.
        </p>

        <!-- Add user form elements -->
        <BFormGroup>
          <label for="email-input" class="form-label">User Email</label>
          <BFormInput
            id="email-input"
            v-model="email"
            type="email"
            required
            placeholder="user@example.com"
          />
        </BFormGroup>

        <BFormGroup class="mt-3">
          <label for="role-select" class="form-label">Role</label>
          <BFormSelect
            id="role-select"
            v-model="role"
            :options="
              props.adminView
                ? OrganizationUserRoleSelectOptions
                : OrganizationMembershipModeSelectOptions
            "
          />
          <p class="text-secondary mt-2 small">
            <strong>Members</strong> can view and use resources but cannot
            manage organization members or settings. <strong>Admins</strong> can
            manage organization members and settings.
            <span v-if="props.adminView">
              <strong>Owners</strong> have full control over the organization,
              including managing members, settings, and resources. Owners cannot
              be removed by admins. Only one owner is allowed per organization.
            </span>
          </p>
        </BFormGroup>

        <BFormGroup class="mt-1">
          <label for="message-textarea" class="form-label">Message</label>
          <BFormTextarea
            id="message-textarea"
            v-model="message"
            placeholder="Add an optional personal message that will be attached to the invitation email."
            rows="3"
          />
        </BFormGroup>

        <BFormGroup class="mt-3">
          <label for="description-textarea" class="form-label"
            >Description</label
          >
          <BFormTextarea
            id="description-textarea"
            v-model="description"
            placeholder="Add an optional description or note about this invitation for internal use."
            rows="3"
            maxlength="255"
          />
        </BFormGroup>

        <!-- Mutation Error Messages -->
        <BAlert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
        >
          {{ mutationError }}
        </BAlert>

        <!-- Modal Actions (custom footer) -->
        <div class="d-flex justify-content-end mt-4 pt-3 border-top">
          <BButton variant="secondary" @click="handleCloseModal" class="me-2">
            <span>Cancel</span>
          </BButton>
          <!-- Add Member Button -->
          <BButton
            type="submit"
            variant="primary"
            :disabled="mutationInProgress || mutationShowSuccess"
          >
            <span v-if="mutationInProgress">
              <b-spinner small label="" class="me-1"></b-spinner>
              Inviting member
            </span>
            <span v-else-if="mutationShowSuccess">
              <font-awesome-icon :icon="['fas', 'check-double']" class="me-1" />
              Member invited
            </span>
            <span v-else>
              <font-awesome-icon :icon="['fas', 'user-plus']" class="me-1" />
              Invite new user
            </span>
          </BButton>
        </div>
      </BForm>
    </div>
  </b-modal>
</template>
